import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';

// Async thunk for fetching aura boards
export const fetchAuraBoards = createAsyncThunk(
  'aura/fetchAuraBoards',
  async ({ businessId, lang = 'en' }) => {
    const response = await nodeAxiosFirebase({
      method: 'POST',
      url: `aura/general`,
      body: { lang },
    });
    return response;
  }
);

// Async thunk for creating/updating aura board
export const saveAuraBoard = createAsyncThunk(
  'aura/saveAuraBoard',
  async ({ auraId, businessId, type, data, lang = 'en', structureId }) => {
    const response = await nodeAxiosFirebase({
      method: 'POST',
      url: `aura/manage`,
      body: {
        auraId,
        businessId,
        type,
        data,
        lang,
        structureId,
      },
    });
    return response;
  }
);

const initialState = {
  boards: [],
  activeStructures: [],
  inactiveStructures: [],
  currentBoard: null,
  selectedBoardId: null,
  currentTab: 0,
  unreadMessagesCount: 0,

  loading: false,
  error: null,
  availableBlocksTypes: [
    {
      id: 'mainData',
      type: 'content',
      title: 'mainData',
      icon: 'User',
      defaultContent: [],
    },
    {
      id: 'listData',
      type: 'list',
      title: 'listData',
      icon: 'ViewList',
      defaultContent: [],
    },
    {
      id: 'recentActivity',
      type: 'action',
      title: 'recentActivity',
      icon: 'Timeline',
      defaultContent: [],
    },
    {
      id: 'messages',
      type: 'action',
      title: 'messages',
      icon: 'Message',
      defaultContent: [],
    },
    {
      id: 'freeText',
      type: 'action',
      title: 'freeText',
      icon: 'FormatSize',
      defaultContent: '',
    },
    {
      id: 'freeImage',
      type: 'action',
      title: 'freeImage',
      icon: 'Image',
      defaultContent: [],
    },
    {
      id: 'formFill',
      type: 'action',
      title: 'formFill',
      icon: 'ViewAgenda',
      defaultContent: [],
    },
    {
      id: 'actionBtn',
      type: 'action',
      title: 'actionBtn',
      icon: 'Gamepad',
      defaultContent: [],
    },
    {
      id: 'chartData',
      type: 'chart',
      title: 'chartData',
      icon: 'BarChart',
      defaultContent: [],
    },
    {
      id: 'counterData',
      type: 'chart',
      title: 'counterData',
      icon: 'Functions',
      defaultContent: [],
    },
    {
      id: 'customComponent',
      type: 'custom',
      title: 'customComponent',
      icon: 'IntegrationInstructions',
      defaultContent: [],
    },
  ],
};

const auraSlice = createSlice({
  name: 'aura',
  initialState,
  reducers: {
    setCurrentBoard: (state, action) => {
      state.currentBoard = action.payload;
      state.selectedBoardId = action.payload?.id;
    },
    setCurrentTab: (state, action) => {
      state.currentTab = action.payload;
    },
    updateBoardGeneral: (state, action) => {
      if (state.currentBoard) {
        state.currentBoard = { ...state.currentBoard, ...action.payload };
      }
    },
    addTab: (state, action) => {
      if (state.currentBoard) {
        if (!state.currentBoard.tabs) {
          state.currentBoard.tabs = [];
        }
        state.currentBoard.tabs.push(action.payload);
      }
    },
    updateTab: (state, action) => {
      const { tabIndex, updates } = action.payload;
      if (state.currentBoard?.tabs?.[tabIndex]) {
        state.currentBoard.tabs[tabIndex] = {
          ...state.currentBoard.tabs[tabIndex],
          ...updates,
        };
      }
    },
    removeTab: (state, action) => {
      const tabIndex = action.payload;
      if (state.currentBoard?.tabs) {
        state.currentBoard.tabs = state.currentBoard.tabs.filter(
          (_, index) => index !== tabIndex
        );
        if (
          state.currentTab >= state.currentBoard.tabs.length &&
          state.currentBoard.tabs.length > 0
        ) {
          state.currentTab = state.currentBoard.tabs.length - 1;
        } else if (state.currentBoard.tabs.length === 0) {
          state.currentTab = 0;
        }
      }
    },
    addBlock: (state, action) => {
      const { tabIndex, block } = action.payload;
      if (state.currentBoard?.tabs?.[tabIndex]) {
        if (!state.currentBoard.tabs[tabIndex].blocks) {
          state.currentBoard.tabs[tabIndex].blocks = [];
        }
        state.currentBoard.tabs[tabIndex].blocks.push({
          ...block,
          i: `block_${Date.now()}`,
          x: 0,
          y: 0,
          w: 6,
          h: 4,
        });
      }
    },
    updateBlock: (state, action) => {
      const { tabIndex, blockId, updates } = action.payload;
      const tab = state.currentBoard?.tabs?.[tabIndex];
      if (tab?.blocks) {
        const blockIndex = tab.blocks.findIndex((block) => block.i === blockId);
        if (blockIndex !== -1) {
          tab.blocks[blockIndex] = { ...tab.blocks[blockIndex], ...updates };
        }
      }
    },
    removeBlock: (state, action) => {
      const { tabIndex, blockId } = action.payload;
      const tab = state.currentBoard?.tabs?.[tabIndex];
      if (tab?.blocks) {
        tab.blocks = tab.blocks.filter((block) => block.i !== blockId);
      }
    },
    updateBlockLayout: (state, action) => {
      const { tabIndex, layout } = action.payload;
      const tab = state.currentBoard?.tabs?.[tabIndex];
      if (tab?.blocks) {
        layout.forEach((layoutItem) => {
          const block = tab.blocks.find((b) => b.i === layoutItem.i);
          if (block) {
            block.x = layoutItem.x;
            block.y = layoutItem.y;
            block.w = layoutItem.w;
            block.h = layoutItem.h;
          }
        });
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    setUnreadMessagesCount: (state, action) => {
      state.unreadMessagesCount = action.payload;
    },
    decrementUnreadMessagesCount: (state) => {
      if (state.unreadMessagesCount > 0) {
        state.unreadMessagesCount -= 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuraBoards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAuraBoards.fulfilled, (state, action) => {
        state.loading = false;
        state.boards = action.payload.boards || [];
        state.unreadMessagesCount = action.payload.unreadMessagesCount || 0;
        state.activeStructures = Array.isArray(action.payload.activeStructures)
          ? action.payload.activeStructures
          : state.activeStructures;
        state.inactiveStructures = Array.isArray(action.payload.inactiveStructures)
          ? action.payload.inactiveStructures
          : state.inactiveStructures;
      })
      .addCase(fetchAuraBoards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(saveAuraBoard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveAuraBoard.fulfilled, (state, action) => {
        state.loading = false;
        // Update boards list if we have the updated board
        if (action.payload.board) {
          const index = state.boards.findIndex(
            (b) => b.id === action.payload.board.id
          );
          if (index !== -1) {
            state.boards[index] = action.payload.board;
          } else {
            state.boards.push(action.payload.board);
          }
        }
      })
      .addCase(saveAuraBoard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const {
  setCurrentBoard,
  setCurrentTab,
  updateBoardGeneral,
  addTab,
  updateTab,
  removeTab,
  addBlock,
  updateBlock,
  removeBlock,
  updateBlockLayout,
  clearError,
  setUnreadMessagesCount,
  decrementUnreadMessagesCount,
} = auraSlice.actions;

export default auraSlice.reducer;
